import { EmberInvite } from "game-common/models"
import { Container, Graphics, Text } from "pixi.js"
import { BUTTON_SIZES, HUD_PANEL_FILL } from "./ui_tokens"
import { TextRenderer } from "../text/text_renderer"
import { blendColor, drawFlareGlow, FLARE_COLOR, FLARE_MS, flareIntensity, flareScale } from "./flare"
import { createButton } from "./button_factory"
import { UiButton } from "./ui_button"
import { truncateName } from "./ember_players_panel"

// The prompt an invited player answers: who is asking, and Join / Decline. It docks in the right-hand
// gutter directly under the players chip, above the player list, so the whole "who else is playing"
// business reads as one column.
//
// It is NOT a modal. An invitation is somebody else's idea, arriving while the player is in the middle
// of something -- very likely a wave -- so it must be ignorable. Ignoring it IS an answer: the bar at
// the foot of the panel drains, and at zero the prompt takes itself down without moving anybody. The
// server expires the invitation on the same clock, so the two agree without a second message.
//
// Amber rim, from the same ember-lit family as the role picker and the tutorial bar, because unlike
// everything else in this column it wants an answer.
//
// And because it is the most ignorable thing on screen that most deserves not to be -- it arrives
// unasked, in the corner, while the middle of the screen is a wave -- it ARRIVES with the HUD's
// shared flare (see ./flare), the same three seconds of hot pulsing, glow and rings the players chip
// uses. Only the plate behind the prompt breathes with it: the buttons stay exactly where they are,
// because one of them moves the player to another room and must not slide under a thumb already on
// its way down.
const WIDTH = 186
const PAD = 8
const CORNER = 6
const LINE_GAP = 4
const BUTTON_H = BUTTON_SIZES.extraSmall.height
const BUTTON_GAP = 6
const METER_H = 3
const METER_GAP = 7

const PANEL_COLOR = HUD_PANEL_FILL
const PANEL_ALPHA = 0.9
const RIM = 0xf2a94e
const RIM_ALPHA = 0.7
const TITLE_COLOR = 0xfff3e0
const BODY_COLOR = 0x8d97ad

const ACCEPT_TEXT = 0xdcffe2
const DECLINE_TEXT = 0xd4d8e0
const METER_TRACK = 0x2a2119

// How far the plate swells at the peak of a pulse. Far gentler than the players chip's: this is a
// 186px panel docked against the right edge with a list under it, and it has no room to throw about.
const FLARE_POP = 0.045
// The halo is kept tight for the same reason -- the gutter it sits in is only so wide.
const FLARE_GLOW_SPREAD = 6
const FLARE_RING_SPREAD = 11

// Fallback line height for a Text that measures as zero, which is every Text under a mocked canvas.
// Without it the panel collapses onto its own buttons in any headless render.
const LINE_H = 10

// How much of its time an invitation has left, 0..1. Exported for its own test: the meter is the only
// thing telling the player that doing nothing is a decision with a deadline.
export const inviteProgress = (remainingMs: number, totalMs: number): number => {
    if (!(totalMs > 0)) {
        return 0
    }
    return Math.max(0, Math.min(1, remainingMs / totalMs))
}

export class EmberInvitePrompt extends Container {
    // The plate and its halo ride on their own container so the flare's pop can scale them about the
    // panel's middle while the text and the two buttons stay put. Nothing interactive moves.
    private shell = new Container()
    private glow = new Graphics()
    private plate = new Graphics()
    private meter = new Graphics()
    private title: Text
    private body: Text
    private accept: UiButton
    private decline: UiButton
    private onAnswer: (accepted: boolean) => void

    private invite: EmberInvite | null = null
    private remainingMs = 0
    private totalMs = 0
    private flareMs = 0

    constructor({ onAnswer }: { onAnswer: (accepted: boolean) => void }) {
        super()
        this.onAnswer = onAnswer

        this.title = new TextRenderer().size("tiny").render("") as Text
        this.title.tint = TITLE_COLOR
        this.title.x = PAD
        this.title.y = PAD - 2

        this.body = new TextRenderer().size("tiny").render("wants you at their camp") as Text
        this.body.tint = BODY_COLOR
        this.body.x = PAD
        this.body.y = this.title.y + LINE_H + LINE_GAP

        const buttonW = Math.round((WIDTH - PAD * 2 - BUTTON_GAP) / 2)
        const buttonY = this.body.y + LINE_H + LINE_GAP + 2
        this.accept = createButton({ label: "Join", theme: "ember", preset: "confirm", width: buttonW,
            height: BUTTON_H, size: "extraSmall", textColor: ACCEPT_TEXT, hitPadding: 3, onClick: () => this.answer(true) })
        this.decline = createButton({ label: "Decline", theme: "ember", width: buttonW,
            height: BUTTON_H, size: "extraSmall", textColor: DECLINE_TEXT, hitPadding: 3, onClick: () => this.answer(false) })
        this.accept.position.set(PAD, buttonY)
        this.decline.position.set(PAD + buttonW + BUTTON_GAP, buttonY)

        this.shell.addChild(this.glow, this.plate)
        this.addChild(this.shell, this.title, this.body, this.accept, this.decline, this.meter)
        // Pivot and position on the same point, so the shell covers its own box at rest and swells
        // symmetrically out of the middle under the pop.
        this.shell.pivot.set(WIDTH / 2, this.getHeight() / 2)
        this.shell.position.set(WIDTH / 2, this.getHeight() / 2)
        this.draw()
        this.visible = false
        this.exposeDevTriggers()
    }

    // Browser-console triggers, in the same shape as window.playersFlare (see EmberPlayersChip): an
    // invitation needs a second logged-in player to send one, so there is otherwise no way to look at
    // the arrival this widget is built around.
    //
    //     invitePrompt            // the widget itself: isOpen(), remaining(), answer(true)
    //     inviteFlare("Ash")      // an invitation from Ash, flare and all
    private exposeDevTriggers = () => {
        if (typeof window === "undefined") {
            return
        }
        ;(window as any).invitePrompt = this
        ;(window as any).inviteFlare = (fromName: string = "Ash"): string => {
            this.update({ fromEntityId: "dev", fromName, expiresInMs: 20000 } as EmberInvite)
            return `[inviteFlare] from ${fromName}, ${FLARE_MS}ms`
        }
    }

    // Put the prompt up, or take it down with null. A fresh invitation always restarts the clock --
    // a second host asking replaces the first, exactly as the server's own one-per-guest rule does.
    update = (invite: EmberInvite | null) => {
        this.accept.resetInteraction()
        this.decline.resetInteraction()
        this.invite = invite || null
        if (!this.invite) {
            this.visible = false
            this.remainingMs = 0
            this.totalMs = 0
            return
        }
        this.totalMs = this.invite.expiresInMs > 0 ? this.invite.expiresInMs : 0
        this.remainingMs = this.totalMs
        this.title.text = `${truncateName(this.invite.fromName)}`
        this.body.text = this.invite.playMode === "cooperation" ? "Cooperation: no player hits" : "PVP: unrestricted play"
        this.visible = true
        // Every invitation that goes up is a new one -- update(null) takes the old one down, and a
        // second host asking replaces the first -- so every one of them announces itself.
        this.flareMs = FLARE_MS
        this.draw()
    }

    // Answer it. Public because the buttons are buried in the display tree, and because a caller with
    // a keyboard shortcut or a test has as much right to answer as the pointer does.
    answer = (accepted: boolean) => {
        if (!this.invite) {
            return
        }
        this.invite = null
        this.visible = false
        this.onAnswer(accepted)
    }

    // Drain the clock. Doing nothing is an answer -- at zero the prompt takes itself down, and tells
    // the server nothing: the server is expiring the same invitation on the same clock.
    animate = (dtMs: number) => {
        if (!this.invite) {
            return
        }
        // The flare is drained BEFORE the deadline is consulted, because an invitation that arrived
        // without one (expiresInMs 0) never expires -- and must not therefore sit in the gutter with
        // a plate frozen mid-flare forever.
        const flaring = this.flareMs > 0
        if (flaring) {
            this.flareMs = Math.max(0, this.flareMs - dtMs)
            this.draw()
        }
        if (this.totalMs <= 0) {
            return
        }
        this.remainingMs = Math.max(0, this.remainingMs - dtMs)
        if (this.remainingMs <= 0) {
            this.invite = null
            this.visible = false
            this.flareMs = 0
            return
        }
        // The flare is three seconds out of an invitation's twenty, so most frames only redraw the
        // meter -- and a frame that redrew the whole prompt has already drawn it.
        if (!flaring) {
            this.drawMeter()
        }
    }

    isFlaring = (): boolean => this.flareMs > 0

    isOpen = (): boolean => !!this.invite
    remaining = (): number => this.remainingMs
    // The name the prompt is showing, for a caller outside the display tree.
    inviterLabel = (): string => this.title.text

    getWidth = (): number => WIDTH
    getHeight = (): number => this.accept.y + BUTTON_H + METER_GAP + METER_H + PAD - 2

    private draw = () => {
        const hot = flareIntensity(this.flareMs)
        drawFlareGlow(this.glow, {
            remainingMs: this.flareMs,
            width: WIDTH,
            height: this.getHeight(),
            corner: CORNER,
            spread: FLARE_GLOW_SPREAD,
            ringSpread: FLARE_RING_SPREAD,
        })
        this.plate.clear()
        // The plate only warms -- it stays dark enough to read the two button labels against at the
        // hottest point of the flare. The RIM is what carries the heat.
        this.plate.beginFill(blendColor(PANEL_COLOR, FLARE_COLOR, hot * 0.4), PANEL_ALPHA)
        this.plate.lineStyle(
            1 + hot * 1.5,
            blendColor(RIM, 0xffffff, hot * 0.55),
            Math.min(1, RIM_ALPHA + hot * 0.3),
        )
        this.plate.drawRoundedRect(0, 0, WIDTH, this.getHeight(), CORNER)
        this.plate.endFill()
        this.shell.scale.set(flareScale(this.flareMs, FLARE_POP))
        this.drawMeter()
    }

    private drawMeter = () => {
        const w = WIDTH - PAD * 2
        const y = this.accept.y + BUTTON_H + METER_GAP
        this.meter.clear()
        this.meter.beginFill(METER_TRACK, 0.9)
        this.meter.drawRoundedRect(PAD, y, w, METER_H, 1.5)
        this.meter.endFill()
        const left = inviteProgress(this.remainingMs, this.totalMs)
        if (left > 0) {
            this.meter.beginFill(RIM, 0.95)
            this.meter.drawRoundedRect(PAD, y, Math.max(1, w * left), METER_H, 1.5)
            this.meter.endFill()
        }
    }
}
